/**
 * I5 — Magnetic Card (Lusion 톤)
 *
 * 카드 hover 시 커서 방향으로 6~10px 자기력 + 그림자 깊이 0→16px.
 * 모바일(<768px) / prefers-reduced-motion 자동 OFF.
 *
 * 트리거: <article class="ui-card" data-anim="magnetic">...</article>
 *   • mouseenter/move/leave 에 transform translate 적용
 *   • mouseleave 시 spring out (DUR.slow, EASE.outQuart)
 *
 * 가이드: plan §2.1 / §7.1 A-2d
 */

import { DUR, EASE, isImmersiveCapable } from "./MotionTokens";

const MAX_MAGNET_PX = 8;     // 자기력 최대 변위
const TARGET_SHADOW = "0 16px 40px rgba(15, 76, 129, 0.18)";

export class MagneticCardController {
    private static cleanupFns: Array<() => void> = [];

    static init(): void {
        // 기존 init 결과 청소 (HMR 대비)
        this.cleanupFns.forEach((fn) => fn());
        this.cleanupFns = [];

        if (!isImmersiveCapable()) return;

        const cards = document.querySelectorAll<HTMLElement>('[data-anim="magnetic"]');
        if (!cards.length) return;

        cards.forEach((card) => {
            const cleanup = MagneticCardController.attach(card);
            this.cleanupFns.push(cleanup);
        });
    }

    private static attach(card: HTMLElement): () => void {
        // 부드러운 트랜지션 — JS 가 transform 을 직접 쓰니까 transition 으로 보간
        const initialTransition = card.style.transition;
        const initialShadow = card.style.boxShadow;
        const initialWillChange = card.style.willChange;

        // EASE.outQuart 는 GSAP 이름이라 CSS transition 에는 cubic-bezier 직접 사용
        card.style.transition = `transform ${DUR.base}s cubic-bezier(0.22, 1, 0.36, 1), box-shadow ${DUR.base}s cubic-bezier(0.22, 1, 0.36, 1)`;
        void EASE.outQuart; // 의도적 참조 유지 (단일 출처 원칙 표시)
        card.style.willChange = "transform";

        const onMove = (e: MouseEvent): void => {
            const rect = card.getBoundingClientRect();
            const x = e.clientX - (rect.left + rect.width / 2);
            const y = e.clientY - (rect.top + rect.height / 2);
            // 카드 절반 너비로 정규화 → -1..1
            const nx = Math.max(-1, Math.min(1, x / (rect.width / 2)));
            const ny = Math.max(-1, Math.min(1, y / (rect.height / 2)));
            const tx = nx * MAX_MAGNET_PX;
            const ty = ny * MAX_MAGNET_PX;
            card.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
        };

        const onEnter = (): void => {
            card.style.boxShadow = TARGET_SHADOW;
        };

        const onLeave = (): void => {
            card.style.transform = "translate3d(0, 0, 0)";
            card.style.boxShadow = initialShadow;
        };

        card.addEventListener("mousemove", onMove);
        card.addEventListener("mouseenter", onEnter);
        card.addEventListener("mouseleave", onLeave);

        return () => {
            card.removeEventListener("mousemove", onMove);
            card.removeEventListener("mouseenter", onEnter);
            card.removeEventListener("mouseleave", onLeave);
            card.style.transition = initialTransition;
            card.style.boxShadow = initialShadow;
            card.style.willChange = initialWillChange;
            card.style.transform = "";
        };
    }
}
